import { cn } from '@/lib/utils';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { CircleCheck, CircleX, Info, Plus, Save, Star, Trash2, TriangleAlert, X } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { updateSelectionCountCriteria } from 'rtk/TemplateSlice';
import QuestionAnswersCombobox from 'components/comboboxes/QuestionAnswersCombobox';
import {
  type TQuestionEvaluation,
  type TSelectionCountCriteria,
  EEvaluationType,
} from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  nodeId: string;
  onClose: () => void;
}

const getDefaultEvaluation = (): TQuestionEvaluation => ({
  name: EEvaluationType.Qualified,
  bonus_points: 0,
  points: 0,
  weight: 0,
  weighted: false,
  isDefault: false,
});

const getDefaultCriteria = (): TSelectionCountCriteria => ({
  evaluation: getDefaultEvaluation(),
  condition: {
    min: 1,
  },
});

const SelectionCountEditor = ({ nodeId, onClose, className, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const nodeEvaluation = useAppSelector((state) => state.Template?.present?.c!.evaluation[nodeId]);
  const originalCriteria = nodeEvaluation?.selection_count ?? ([] as TSelectionCountCriteria[]);

  const evaluationTypeOptions = useMemo(
    () => [
      {
        value: EEvaluationType.Qualified,
        label: t('selectionCountEditor.evaluationTypes.qualified'),
        icon: CircleCheck,
      },
      {
        value: EEvaluationType.Disqualified,
        label: t('selectionCountEditor.evaluationTypes.disqualified'),
        icon: CircleX,
      },
      { value: EEvaluationType.Bonus, label: t('selectionCountEditor.evaluationTypes.bonus'), icon: Star },
      { value: EEvaluationType.Warning, label: t('selectionCountEditor.evaluationTypes.warning'), icon: TriangleAlert },
      { value: EEvaluationType.Info, label: t('selectionCountEditor.evaluationTypes.info'), icon: Info },
    ],
    [t],
  );

  const [criteria, setCriteria] = useState<TSelectionCountCriteria[]>(() =>
    originalCriteria.length > 0 ? structuredClone(originalCriteria) : [getDefaultCriteria()],
  );

  const handleSave = () => {
    dispatch(
      updateSelectionCountCriteria({
        nodeId,
        selectionCountCriteria: criteria,
      }),
    );
    onClose();
  };

  const handleAddCriteria = () => {
    setCriteria([...criteria, getDefaultCriteria()]);
  };

  const handleRemoveCriteria = (index: number) => {
    if (criteria.length > 1) {
      setCriteria(criteria.filter((_, i) => i !== index));
    }
  };

  const handleUpdateCondition = (index: number, conditionUpdates: Partial<TSelectionCountCriteria['condition']>) => {
    const newCriteria = [...criteria];
    newCriteria[index] = {
      ...newCriteria[index],
      condition: { ...newCriteria[index].condition, ...conditionUpdates },
    };
    setCriteria(newCriteria);
  };

  const handleUpdateEvaluation = (index: number, evaluationUpdates: Partial<TQuestionEvaluation>) => {
    const newCriteria = [...criteria];
    newCriteria[index] = {
      ...newCriteria[index],
      evaluation: { ...newCriteria[index].evaluation, ...evaluationUpdates },
    };
    setCriteria(newCriteria);
  };

  return (
    <Card className={cn('', className)} {...props}>
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{t('selectionCountEditor.heading')}</h3>
          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm" className="bg-green-600 text-white hover:bg-green-700">
              <Save className="mr-1 h-4 w-4" />
              {t('selectionCountEditor.saveButton')}
            </Button>
            <Button onClick={onClose} variant="outline" size="sm">
              <X className="mr-1 h-4 w-4" />
              {t('selectionCountEditor.cancelButton')}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {criteria.map((criterion, index) => (
            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-start justify-between">
                <span className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-500">
                  {t('selectionCountEditor.ruleNumber', { n: index + 1 })}
                </span>
                {criteria.length > 1 && (
                  <Button
                    onClick={() => handleRemoveCriteria(index)}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-600 hover:border-red-300 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Evaluation Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('selectionCountEditor.evaluationType')}
                  </label>
                  <select
                    value={criterion.evaluation.name}
                    onChange={(e) =>
                      handleUpdateEvaluation(index, {
                        name: e.target.value as EEvaluationType,
                      })
                    }
                    className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  >
                    {evaluationTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bonus Points (if bonus type) */}
                {criterion.evaluation.name === EEvaluationType.Bonus && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">{t('selectionCountEditor.bonusPoints')}</label>
                    <input
                      type="number"
                      value={criterion.evaluation.bonus_points || 0}
                      onChange={(e) =>
                        handleUpdateEvaluation(index, {
                          bonus_points: Number(e.target.value),
                        })
                      }
                      className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                )}
              </div>

              {/* Condition Settings */}
              <div className="mt-4 rounded-md border border-gray-100 bg-white p-3">
                <div className="mb-3 text-sm font-medium text-gray-700">
                  {t('selectionCountEditor.selectionCondition')}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {/* Min selections */}
                  <div className="space-y-2">
                    <label className="text-xs text-gray-600">{t('selectionCountEditor.minimum')}</label>
                    <input
                      type="number"
                      value={criterion.condition.min ?? ''}
                      onChange={(e) => {
                        const num = e.target.value !== '' ? Number(e.target.value) : undefined;
                        handleUpdateCondition(index, { min: num || undefined });
                      }}
                      className="w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder={t('selectionCountEditor.placeholderNoMin')}
                      min="0"
                    />
                  </div>

                  {/* Max selections */}
                  <div className="space-y-2">
                    <label className="text-xs text-gray-600">{t('selectionCountEditor.maximum')}</label>
                    <input
                      type="number"
                      value={criterion.condition.max ?? ''}
                      onChange={(e) => {
                        const num = e.target.value !== '' ? Number(e.target.value) : undefined;
                        handleUpdateCondition(index, { max: num || undefined });
                      }}
                      className="w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder={t('selectionCountEditor.placeholderNoMax')}
                      min="0"
                    />
                  </div>

                  {/* Exact count */}
                  <div className="space-y-2">
                    <label className="text-xs text-gray-600">{t('selectionCountEditor.exactCount')}</label>
                    <input
                      type="number"
                      value={criterion.condition.exactCount ?? ''}
                      onChange={(e) => {
                        const num = e.target.value !== '' ? Number(e.target.value) : undefined;
                        handleUpdateCondition(index, { exactCount: num || undefined });
                      }}
                      className="w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder={t('selectionCountEditor.placeholderNoExact')}
                      min="0"
                    />
                  </div>
                </div>

                {/* Require all options */}
                <div className="mt-3 flex items-center">
                  <input
                    type="checkbox"
                    id={`require-all-${index}`}
                    checked={criterion.condition.requiresAllOptions || false}
                    onChange={(e) =>
                      handleUpdateCondition(index, {
                        requiresAllOptions: e.target.checked,
                      })
                    }
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`require-all-${index}`} className="text-sm text-gray-700">
                    {t('selectionCountEditor.mustSelectAll')}
                  </label>
                </div>

                {/* Negate condition */}
                <div className="mt-3 flex items-center">
                  <input
                    type="checkbox"
                    id={`negate-${index}`}
                    checked={criterion.condition.negate || false}
                    onChange={(e) =>
                      handleUpdateCondition(index, {
                        negate: e.target.checked,
                      })
                    }
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`negate-${index}`} className="text-sm text-gray-700">
                    {/* TODO(i18n): Category 4 — sentence contains <strong>not</strong> mid-text; word order differs by language. Use <Trans> with a tagged placeholder in a follow-up commit. */}
                    Negate condition (apply evaluation when condition is <strong>not</strong> met)
                  </label>
                </div>

                {/* Only specific option IDs (exact match) */}
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      className={cn(
                        'text-sm font-medium',
                        (criterion.condition.includesOptionIds?.length ?? 0) > 0 ? 'text-gray-400' : 'text-gray-700',
                      )}
                    >
                      {t('selectionCountEditor.onlyExactOptions')}
                    </label>
                    {Array.isArray(criterion.condition.onlyOptionIds) &&
                      criterion.condition.onlyOptionIds.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 border-gray-300 text-gray-700"
                          onClick={() =>
                            handleUpdateCondition(index, {
                              onlyOptionIds: [],
                            })
                          }
                        >
                          {t('selectionCountEditor.clearButton')}
                        </Button>
                      )}
                  </div>
                  <QuestionAnswersCombobox
                    nodeId={nodeId}
                    isMulti
                    disabled={(criterion.condition.includesOptionIds?.length ?? 0) > 0}
                    value={criterion.condition.onlyOptionIds ?? []}
                    onValueChange={(val) => {
                      const values = Array.isArray(val) ? val : val ? [val] : [];
                      handleUpdateCondition(index, {
                        onlyOptionIds: values.length ? values : [],
                      });
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('selectionCountEditor.onlyExactOptionsHint')}</p>
                </div>

                {/* Must include option IDs (superset allowed) */}
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      className={cn(
                        'text-sm font-medium',
                        (criterion.condition.onlyOptionIds?.length ?? 0) > 0 ? 'text-gray-400' : 'text-gray-700',
                      )}
                    >
                      {t('selectionCountEditor.mustIncludeOptions')}
                    </label>
                    {Array.isArray(criterion.condition.includesOptionIds) &&
                      criterion.condition.includesOptionIds.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 border-gray-300 text-gray-700"
                          onClick={() =>
                            handleUpdateCondition(index, {
                              includesOptionIds: [],
                            })
                          }
                        >
                          {t('selectionCountEditor.clearButton')}
                        </Button>
                      )}
                  </div>
                  <QuestionAnswersCombobox
                    nodeId={nodeId}
                    isMulti
                    disabled={(criterion.condition.onlyOptionIds?.length ?? 0) > 0}
                    value={criterion.condition.includesOptionIds ?? []}
                    onValueChange={(val) => {
                      const values = Array.isArray(val) ? val : val ? [val] : [];
                      handleUpdateCondition(index, {
                        includesOptionIds: values.length ? values : [],
                      });
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('selectionCountEditor.mustIncludeOptionsHint')}</p>
                </div>

                {/* Ignore option IDs (excluded from the count/comparison) */}
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      {t('selectionCountEditor.ignoreOptions')}
                    </label>
                    {Array.isArray(criterion.condition.ignoreOptionIds) &&
                      criterion.condition.ignoreOptionIds.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 border-gray-300 text-gray-700"
                          onClick={() =>
                            handleUpdateCondition(index, {
                              ignoreOptionIds: [],
                            })
                          }
                        >
                          {t('selectionCountEditor.clearButton')}
                        </Button>
                      )}
                  </div>
                  <QuestionAnswersCombobox
                    nodeId={nodeId}
                    isMulti
                    value={criterion.condition.ignoreOptionIds ?? []}
                    onValueChange={(val) => {
                      const values = Array.isArray(val) ? val : val ? [val] : [];
                      handleUpdateCondition(index, {
                        ignoreOptionIds: values.length ? values : [],
                      });
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('selectionCountEditor.ignoreOptionsHint')}</p>
                </div>
              </div>
            </div>
          ))}

          <Button
            onClick={handleAddCriteria}
            variant="outline"
            className="w-full border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('selectionCountEditor.addNewCriteria')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SelectionCountEditor;
